import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'

// --- Types ---

export type Theme = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

export interface ThemeContextValue {
  theme: Theme
  setTheme: (theme: Theme) => void
  /**
   * Weiche Backend-Präferenz (account.theme): wird NUR angewandt, solange der
   * Nutzer auf diesem Gerät keine explizite Wahl getroffen hat (kein
   * localStorage-Eintrag). Schreibt selbst NIE in localStorage — dadurch bleibt
   * eine explizite lokale Wahl dauerhaft führend und Route-/Layout-Remounts
   * können das Theme nicht zurücksetzen (Mac-Finding W2-1).
   */
  applyBackendTheme: (theme: Theme) => void
  resolvedTheme: ResolvedTheme
}

// --- Constants ---

const STORAGE_KEY = 'ht-theme'
/**
 * W3-F1: Spiegel der weichen Backend-Präferenz (account.theme).
 * Bewusst ein SEPARATER Key — so bleibt STORAGE_KEY exklusiv der expliziten
 * Nutzerwahl vorbehalten (W2-1 darf nicht regressieren), das FOUC-Inline-Script
 * in index.html kann die BE-Präferenz aber schon vor dem ersten Frame lesen.
 * Priorität überall identisch: ht-theme (explizit) ?? ht-theme-backend ?? System.
 */
const BACKEND_STORAGE_KEY = 'ht-theme-backend'
// eslint-disable-next-line react-refresh/only-export-components
export const VALID_THEMES: Theme[] = ['light', 'dark', 'system']

// --- Helpers ---

function readValidTheme(key: string): Theme | null {
  try {
    const stored = localStorage.getItem(key)
    return VALID_THEMES.includes(stored as Theme) ? (stored as Theme) : null
  } catch {
    // Private-Mode / blockierter Storage — Theme bleibt "system"
    return null
  }
}

/**
 * Reads and validates the stored theme preference from localStorage.
 * Explicit local choice wins; the mirrored backend preference is the fallback;
 * otherwise 'system'. Same precedence as the FOUC script in index.html.
 * SSR note: replace localStorage with cookie-read on server side if SSR is introduced.
 */
function getInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'system'
  return readValidTheme(STORAGE_KEY) ?? readValidTheme(BACKEND_STORAGE_KEY) ?? 'system'
}

/**
 * Resolves the effective (visual) theme from the stored intent.
 * 'system' is resolved via prefers-color-scheme — never returned as-is.
 */
function resolveTheme(theme: Theme): ResolvedTheme {
  if (theme !== 'system') return theme
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/**
 * Applies the resolved theme to <html data-theme="…">.
 * Always sets 'light' or 'dark' — never 'system'.
 */
function applyTheme(resolved: ResolvedTheme): void {
  document.documentElement.dataset.theme = resolved
}

// --- Context ---

const ThemeContext = createContext<ThemeContextValue | null>(null)

// --- Provider ---

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme)
  // resolvedTheme is React state, not derived — so consumers see live OS-preference
  // updates from the matchMedia handler (W0.3 QG F-1: Toggle would otherwise display
  // stale resolvedTheme until next React render).
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolveTheme(theme))

  // Sync resolvedTheme whenever theme changes (light/dark/system).
  // setState-in-effect is intentional here: resolvedTheme must be React state (not derived)
  // so matchMedia listener can update it independently (W0.3 QG F-1).
  // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: derived state sync
  useEffect(() => { setResolvedTheme(resolveTheme(theme)) }, [theme])

  // Apply data-theme to <html> whenever resolvedTheme changes
  useEffect(() => {
    applyTheme(resolvedTheme)
  }, [resolvedTheme])

  // Register/remove matchMedia listener — only active when theme === 'system'
  useEffect(() => {
    if (theme !== 'system') return

    const mq = window.matchMedia('(prefers-color-scheme: dark)')

    const handler = (e: MediaQueryListEvent) => {
      // Update React state — applyTheme runs via the effect above.
      setResolvedTheme(e.matches ? 'dark' : 'light')
    }

    mq.addEventListener('change', handler)

    return () => {
      mq.removeEventListener('change', handler)
    }
  }, [theme])

  const setTheme = (next: Theme) => {
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Storage blockiert — Theme gilt dann nur für diese Session
    }
    setThemeState(next)
  }

  // W2-1: Backend-Präferenz nur als Fallback ohne explizite lokale Wahl.
  // Kein Write auf STORAGE_KEY — sonst würde die BE-Präferenz fälschlich zur
  // "expliziten" Wahl und Cross-Device-Sync bräche beim nächsten App-Start.
  // W3-F1: Stattdessen Spiegelung auf BACKEND_STORAGE_KEY, damit das
  // FOUC-Script beim nächsten Reload dasselbe Theme setzt wie React —
  // ohne die Priorität "explizite Wahl schlägt Backend" anzutasten.
  const applyBackendTheme = useCallback((next: Theme) => {
    // Nur ein GÜLTIGER localStorage-Wert zählt als explizite Wahl —
    // Altlasten wie 'auto' blockieren den Backend-Fallback nicht.
    if (readValidTheme(STORAGE_KEY) !== null) return
    if (!VALID_THEMES.includes(next)) return
    try {
      localStorage.setItem(BACKEND_STORAGE_KEY, next)
    } catch {
      // Storage blockiert — dann bleibt es beim FOUC-Fallback "system"
    }
    setThemeState(next)
  }, [])

  return (
    <ThemeContext.Provider value={{ theme, setTheme, applyBackendTheme, resolvedTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

// --- Hook ---

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (ctx === null) {
    throw new Error('useTheme must be used within ThemeProvider')
  }
  return ctx
}
